/**
 * Chord Progression References
 *
 * Harmonic function descriptions for each scale degree chord.
 * Helps users understand the role each chord plays in a progression.
 */

export interface DegreeReference {
    degree: number;
    numeral: string;
    name: string;
    quality: string;
    function: string;
    character: string;
}

export const degreeReferences: Record<number, DegreeReference> = {
    1: {
        degree: 1,
        numeral: "I",
        name: "Tonic",
        quality: "Major",
        function: "Home base, resolution",
        character: "Stable, complete, at rest"
    },
    2: {
        degree: 2,
        numeral: "ii",
        name: "Supertonic",
        quality: "Minor",
        function: "Pre-dominant, leads to V",
        character: "Soft tension, moving forward"
    },
    3: {
        degree: 3,
        numeral: "iii",
        name: "Mediant",
        quality: "Minor",
        function: "Tonic substitute",
        character: "Gentle, somewhat stable"
    },
    4: {
        degree: 4,
        numeral: "IV",
        name: "Subdominant",
        quality: "Major",
        function: "Away from home",
        character: "Bright tension, uplifting"
    },
    5: {
        degree: 5,
        numeral: "V",
        name: "Dominant",
        quality: "Major",
        function: "Strongest pull to I",
        character: "Tense, wants to resolve"
    },
    6: {
        degree: 6,
        numeral: "vi",
        name: "Submediant",
        quality: "Minor",
        function: "Tonic substitute, relative minor",
        character: "Darker, emotional"
    },
    7: {
        degree: 7,
        numeral: "vii°",
        name: "Leading Tone",
        quality: "Diminished",
        function: "Dominant substitute",
        character: "Very unstable, rare"
    }
};

// Helper to get reference by degree number
export const getDegreeReference = (degree: number): DegreeReference | undefined => {
    return degreeReferences[degree];
};

// Helper to get all degree references as array
export const getAllDegreeReferences = (): DegreeReference[] => {
    return Object.values(degreeReferences);
};
